import { BlobServiceClient } from "@azure/storage-blob";
import { sasToken, containerName, storageAccountName } from "./api/index.js";
import API from "./api/index.js";

/**
 * Snapshot Blob Service
 *
 * Uploads captured snapshot dataURLs to Azure Blob Storage and returns
 * publicly-accessible URLs.  On load, verifies the URLs are reachable.
 *
 * Blob path convention:
 *   snapshots/{contactId}/snapshot-2d-{timestamp}.png
 *   snapshots/{contactId}/snapshot-3d-{timestamp}.png
 */

// ── helpers ──────────────────────────────────────────────────────────

/** Convert a base-64 dataURL to a Blob */
function dataURLtoBlob(dataURL) {
  const [header, base64] = dataURL.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/** Build a BlobServiceClient from the current SAS token */
function getBlobServiceClient() {
  const token = sasToken;
  if (!token) throw new Error('[SnapshotBlob] SAS token not available — call UPDATE_SAS_TOKEN first');
  return new BlobServiceClient(
    `https://${storageAccountName}.blob.core.windows.net/?${token}`
  );
}

// ── public API ───────────────────────────────────────────────────────

/**
 * Ensure we have a valid SAS token before any upload.
 * Always refreshes the token since it may have expired.
 * Re-throws on failure so callers can handle gracefully.
 */
export async function ensureSasToken() {
  try {
    await API.UPDATE_SAS_TOKEN();
  } catch (err) {
    console.warn('[SnapshotBlob] SAS token unavailable:', err?.message);
    throw new Error('Failed to fetch SAS token for snapshot upload');
  }
}

/**
 * Upload a single dataURL snapshot to Azure Blob Storage.
 *
 * @param {string} dataURL   base-64 PNG dataURL from canvas.toDataURL()
 * @param {string} contactId the current contact / design ID
 * @param {'2d'|'3d'} mode   snapshot mode — used in the blob name
 * @returns {Promise<string>} the public URL (without SAS token) of the uploaded blob
 */
export async function uploadSnapshot(dataURL, contactId, mode) {
  await ensureSasToken();

  const blob = dataURLtoBlob(dataURL);
  const timestamp = Date.now();
  const blobName = `snapshots/${contactId}/snapshot-${mode}-${timestamp}.png`;

  const blobServiceClient = getBlobServiceClient();
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(blobName);

  await blockBlobClient.uploadData(blob, {
    blobHTTPHeaders: { blobContentType: 'image/png' },
  });

  // Return URL without SAS token — the container allows public read or
  // the consumer will append a fresh SAS when needed.
  const publicUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blobName}`;
  console.log(`[SnapshotBlob] Uploaded ${mode} → ${publicUrl}  (${blob.size} bytes)`);
  return publicUrl;
}

/**
 * Upload both 2D and 3D snapshots in parallel.
 *
 * @param {{ snapshot2D: string, snapshot3D: string }} snapshots
 * @param {string} contactId
 * @returns {Promise<{ snapshot2DUrl: string, snapshot3DUrl: string }>}
 */
export async function uploadSnapshots(snapshots, contactId) {
  const [snapshot2DUrl, snapshot3DUrl] = await Promise.all([
    uploadSnapshot(snapshots.snapshot2D, contactId, '2d'),
    uploadSnapshot(snapshots.snapshot3D, contactId, '3d'),
  ]);
  return { snapshot2DUrl, snapshot3DUrl };
}